import React from 'react';
import getConfig from 'next/config';
import { Stack, StackItem } from '@domain-group/fe-blueprint-ui/layout';
import { urlUtil } from '@domain-group/fe-helper';
import Illustration from '@domain-group/fe-co-brand-illustrations';
import {
  FAA_LANDING_PAGE_MODULE,
  entryPoint,
  trackAppraisalFunnelEntry,
} from '../../../../tracking/landing-page';
import type {
  FaaLandingPageModule,
  CtaDriverTrackingProps,
} from '../../../../tracking/landing-page';
import CtaItem from './components/cta-item';
import * as styles from './call-to-actions-2026.style';

interface CallToActions2026Props {
  onModuleRendered?: (module: FaaLandingPageModule) => void;
  modules?: FaaLandingPageModule[];
}

interface CtaConfig {
  module: FaaLandingPageModule;
  icon: React.ReactNode;
  href: string;
  label: string;
  testId?: string;
  ctaDriver: CtaDriverTrackingProps;
  onAdditionalClick?: () => void;
}

const CallToActions2026 = ({
  onModuleRendered,
  modules,
}: CallToActions2026Props): JSX.Element => {
  const { publicRuntimeConfig } = getConfig();
  const baseUrlWithProtocol = publicRuntimeConfig?.BASE_URL || '';

  const ctaConfigs: CtaConfig[] = [
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_GET_APPRAISAL,
      icon: (
        <Illustration
          type="spot"
          shape="circle"
          name="propertyReport"
          size="regular"
        />
      ),
      href: urlUtil.createUrlWithParams({
        baseUrl: baseUrlWithProtocol,
        path: '/services/appraisal',
        params: {
          exitUrl: `${baseUrlWithProtocol}/real-estate-agents`,
          'entry-point': entryPoint,
        },
      }),
      label: 'Looking to sell? Get a free appraisal',
      testId: 'appraisal-link',
      ctaDriver: {
        interactionCTA: 'Looking to sell? Get a free appraisal',
        elementListPosition: 1,
        elementTargetCategoryPrimary: 'Request an Appraisal',
        elementTargetPageType: 'Funnel',
        elementTitle: 'Looking to sell? Get a free appraisal',
      },
      onAdditionalClick: trackAppraisalFunnelEntry,
    },
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_TRACK_HOME,
      icon: (
        <Illustration
          type="spot"
          shape="circle"
          name="equityHome"
          size="regular"
        />
      ),
      href: `${baseUrlWithProtocol}/owners`,
      label: 'Follow your property to understand its value',
      ctaDriver: {
        interactionCTA: 'Follow your property to understand its value',
        elementListPosition: 2,
        elementTargetCategoryPrimary: 'Owners',
        elementTargetPageType: 'Dashboard',
        elementTitle: 'Follow your property to understand its value',
      },
    },
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_RECENTLY_SOLD,
      icon: (
        <Illustration
          type="spot"
          shape="circle"
          name="auction"
          size="regular"
        />
      ),
      href: `${baseUrlWithProtocol}/?mode=sold`,
      label: 'See recently sold properties in your area',
      ctaDriver: {
        interactionCTA: 'See recently sold properties in your area',
        elementListPosition: 3,
        elementTargetCategoryPrimary: 'Sold',
        elementTargetPageType: 'Homepage',
        elementTitle: 'See recently sold properties in your area',
      },
    },
  ];

  return (
    <Stack
      as="ul"
      vertical={{ compact: true, regular: false }}
      wrap
      gap={{ compact: 's', regular: 'm' }}
      css={styles.callToActions}
    >
      {ctaConfigs.map((config) => (
        <StackItem
          as="li"
          key={config.module}
          grow={1}
          basis={{ regular: '320px' }}
        >
          <CtaItem
            module={config.module}
            icon={config.icon}
            href={config.href}
            styles={styles}
            testId={config.testId}
            ctaDriver={config.ctaDriver}
            onModuleRendered={onModuleRendered}
            onAdditionalClick={config.onAdditionalClick}
            modules={modules}
          >
            <p css={styles.text}>{config.label}</p>
          </CtaItem>
        </StackItem>
      ))}
    </Stack>
  );
};

export default CallToActions2026;
